<template>
  <DocumentsAISummaryMessageFromAI
    ref="summaryMessage"
    data-locator="summary-content"
    :message-date="documentSummary.createdAt"
  >
    <AppTextWriter
      v-if="withTextAnimation"
      :text="documentSummary.summary"
      @start="areActionsDisplayed = false"
      @end="areActionsDisplayed = true"
    />
    <p v-else>{{ documentSummary.summary }}</p>

    <DocumentsAISummaryMessageActions
      v-if="areActionsDisplayed"
      :summary-feedback="summaryFeedback"
      @copy="copySummary"
      @downvote="downVoteSummary"
      @upvote="upVoteSummary"
    />
  </DocumentsAISummaryMessageFromAI>
</template>

<script lang="ts">
import { ComponentPublicInstance, defineComponent, PropType } from 'vue'
import { mapGetters } from 'vuex'

import AppTextWriter from '@/common/AppTextWriter.vue'
import { toast } from '@/common/utils/toast'
import { type DocumentAISummary, SummaryFeedback } from '@/project/documents/pane/DocumentsAISummaryPane.vue'
import aiSummariesService from '@/services/ai-summaries.service'

import DocumentsAISummaryMessageActions from './DocumentsAISummaryMessageActions.vue'
import DocumentsAISummaryMessageFromAI from './DocumentsAISummaryMessageFromAI.vue'

export default defineComponent({
  name: 'DocumentsAISummaryChatMessage',
  components: {
    DocumentsAISummaryMessageActions,
    DocumentsAISummaryMessageFromAI,
    AppTextWriter,
  },
  props: {
    documentSummary: {
      type: Object as PropType<DocumentAISummary>,
      required: true,
    },
    withTextAnimation: {
      type: Boolean,
      default: false,
    },
    displayActions: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      areActionsDisplayed: true,
      summaryFeedback: this.documentSummary.feedback,
      messageResizeObserver: null as ResizeObserver | null,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
  },
  mounted () {
    const summaryMessageComponent = this.$refs['summaryMessage'] as ComponentPublicInstance<typeof DocumentsAISummaryMessageFromAI>

    if (this.withTextAnimation && summaryMessageComponent) {
      this.messageResizeObserver = new ResizeObserver(this.scrollToSummaryEnd)
      this.messageResizeObserver.observe(summaryMessageComponent.$el)
    }
  },
  beforeUnmount () {
    if (this.messageResizeObserver) {
      this.messageResizeObserver.disconnect()
    }
  },
  methods: {
    async copySummary () {
      await navigator.clipboard.writeText(this.documentSummary.summary)
    },
    async upVoteSummary () {
      try {
        await aiSummariesService.patchAISummaryUpvote(this.roomMnemo, this.documentSummary.id)
        this.summaryFeedback = SummaryFeedback.FEEDBACK_POSITIVE
      } catch {
        toast.error(this.$t('common.msgFailErrorOccurred'))
      }
    },
    async downVoteSummary () {
      try {
        await aiSummariesService.patchAISummaryDownvote(this.roomMnemo, this.documentSummary.id)
        this.summaryFeedback = SummaryFeedback.FEEDBACK_NEGATIVE
      } catch {
        toast.error(this.$t('common.msgFailErrorOccurred'))
      }
    },
    async scrollToSummaryEnd () {
      await this.$nextTick()
      const summaryMessage = this.$refs['summaryMessage'] as ComponentPublicInstance<typeof DocumentsAISummaryMessageFromAI>
      (summaryMessage.$el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'end' })
    },
  },
})
</script>
